import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import LabeledProgressBar from "../../common/ui/labeled-progress/LabeledProgressBar";
import { fetchApprovalVelocity, handleTimesheetApiError } from "./timesheetApi";

/**
 * Approval velocity for the selected period (matches queue month/week scope).
 * Shown below summary stats as a linear progress bar (same component pattern as Time log).
 * @param {{ periodStart: Date, periodEnd: Date, refreshKey?: number }} props
 */
const TimeSheetApprovalVelocitySection = ({ periodStart, periodEnd, refreshKey = 0 }) => {
  const { t } = useTranslation();

  const [velocity, setVelocity] = useState(null);
  const [loading, setLoading] = useState(false);

  const periodKey = useMemo(() => {
    const a = periodStart instanceof Date ? periodStart.getTime() : 0;
    const b = periodEnd instanceof Date ? periodEnd.getTime() : 0;
    return `${a}-${b}`;
  }, [periodStart, periodEnd]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchApprovalVelocity({ periodStart, periodEnd })
      .then((data) => {
        if (!cancelled) setVelocity(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setVelocity(null);
          handleTimesheetApiError(err, t("app.timesheets.approvals.velocityLoadFailed"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [periodKey, periodStart, periodEnd, refreshKey, t]);

  const velocityPct = useMemo(() => {
    if (!velocity) return 0;
    const p = Number(velocity.approvalVelocityPercent);
    if (!Number.isFinite(p)) return 0;
    return Math.min(100, Math.max(0, p));
  }, [velocity]);

  return (
    <section className="ts-appr-velocity-panel" aria-label={t("app.timesheets.approvals.velocityTitle")}>
      {loading ? (
        <p className="text-muted small mb-0">{t("app.timesheets.approvals.velocityLoading")}</p>
      ) : (
        <LabeledProgressBar
          label={t("app.timesheets.approvals.velocityGaugeLabel")}
          valuePct={velocityPct}
          labelId="ts-appr-velocity-progress-lbl"
        />
      )}
    </section>
  );
};

export default TimeSheetApprovalVelocitySection;
